import React, { useState, useEffect } from 'react';
import { Sidebar } from './components/Sidebar';
import { Dashboard } from './components/Dashboard';
import { JobPlanningView, ServiceCRMView, AssetManagerView, RentalManagerView } from './components/modules/OperationsViews';
import { SalesCRMView, HRManagerView, ExpenseTrackerView, ContractorMgrView } from './components/modules/BusinessViews';
import { HSSEMgrView, DocControlView } from './components/modules/SafetyComplianceViews';
import { AdminUsersView } from './components/modules/AdminViews';
import { ModuleType } from './types';
import { Bell, Search, UserCircle, Menu, Bot, LogOut } from 'lucide-react';
import { AuthProvider, useAuth } from './services/AuthContext';
import Login from './components/Login';

const startGeminiChat = () => {
  console.log("Gemini Chat feature is currently under development.");
  alert("Gemini Chat feature is currently under development.");
};

const AppContent: React.FC = () => {
  const { user, logout, isAuthenticated, isInitializing } = useAuth();
  const [activeModule, setActiveModule] = useState<ModuleType>(() => {
    const saved = localStorage.getItem('titan_active_module');
    return (saved as ModuleType) || ModuleType.DASHBOARD;
  });
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    localStorage.setItem('titan_active_module', activeModule);
  }, [activeModule]);

  if (isInitializing) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-titan-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  const renderModule = () => {
    switch (activeModule) {
      case ModuleType.DASHBOARD: return <Dashboard />;
      case ModuleType.JOB_PLANNING: return <JobPlanningView />;
      case ModuleType.SERVICE_CRM: return <ServiceCRMView />;
      case ModuleType.ASSET_MGR: return <AssetManagerView />;
      case ModuleType.RENTAL_MGR: return <RentalManagerView />;
      case ModuleType.SALES_CRM: return <SalesCRMView />;
      case ModuleType.HR_MGR: return <HRManagerView />;
      case ModuleType.EXPENSE: return <ExpenseTrackerView />;
      case ModuleType.CONTRACTOR: return <ContractorMgrView />;
      case ModuleType.HSSE: return <HSSEMgrView />;
      case ModuleType.DOC_CONTROL: return <DocControlView />;
      case ModuleType.SYSTEM_ADMIN: return <AdminUsersView />;
      default: return <Dashboard />;
    }
  };

  return (
    <div className="flex h-screen bg-slate-950 text-slate-200 font-sans selection:bg-titan-500 selection:text-white">
      <Sidebar activeModule={activeModule} setActiveModule={setActiveModule} isOpen={sidebarOpen} />
      
      <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
        {/* Top Header */}
        <header className="h-16 bg-slate-900/80 backdrop-blur border-b border-slate-800 flex items-center justify-between px-6 sticky top-0 z-40">
           <div className="flex items-center gap-4">
             <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-slate-400 hover:text-white transition">
               <Menu className="w-6 h-6" />
             </button>
             <div className="relative hidden md:block">
               <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-500" />
               <input 
                 type="text" 
                 placeholder="Global Search..." 
                 className="bg-slate-800 border border-slate-700 rounded-full pl-10 pr-4 py-1.5 text-sm focus:ring-2 focus:ring-titan-500 focus:border-transparent outline-none w-64 transition-all focus:w-80"
               />
             </div>
           </div>

           <div className="flex items-center gap-4">
             <div className="hidden lg:flex flex-col items-end mr-4">
                <span className="text-xs font-bold text-titan-500 uppercase">{user?.tenantName}</span>
                <span className="text-[10px] text-slate-500">{user?.role}</span>
             </div>

             <button 
                onClick={startGeminiChat}
                className="flex items-center gap-2 px-3 py-1.5 bg-titan-500/10 text-titan-500 rounded-full text-sm font-medium hover:bg-titan-500 hover:text-white transition group border border-titan-500/20"
             >
                <Bot className="w-4 h-4" />
                <span className="hidden md:inline">Ask Titan AI</span>
             </button>

             <div className="h-8 w-px bg-slate-700 mx-2"></div>

             <button className="relative text-slate-400 hover:text-white transition">
               <Bell className="w-5 h-5" />
               <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse"></span>
             </button>
             
             <div className="flex items-center gap-3 pl-2 group relative">
                <div className="flex items-center gap-3 cursor-pointer hover:bg-slate-800 p-1.5 rounded-lg transition">
                  <div className="text-right hidden md:block">
                    <div className="text-sm font-semibold text-white">{user?.firstName} {user?.lastName}</div>
                    <div className="text-xs text-slate-400">{user?.email}</div>
                  </div>
                  <UserCircle className="w-9 h-9 text-slate-400 group-hover:text-white transition" />
                </div>
                
                {/* Simple Dropdown for logout */}
                <div className="absolute right-0 top-full mt-2 w-48 bg-slate-900 border border-slate-800 rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 p-2">
                  <button 
                    onClick={logout}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-900/20 rounded-lg transition"
                  >
                    <LogOut className="w-4 h-4" /> Sign Out
                  </button>
                </div>
             </div>
           </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto p-6 bg-slate-950">
          <div className="max-w-7xl mx-auto">
            {renderModule()}
          </div>
        </main>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;
